<template>
  <div class="audience-preview-section">
    <div class="flex flex-column mb-2">
      <h4 class="text-sm font-semibold text-blue-800 mb-2">🎯 Audience Preview</h4>
      <button
        :disabled="checkingTargetCount"
        @click="checkTargetedUsersCount"
        class="custom-button"
        style="border: 2px solid #3b82f6; color: #3b82f6; background: transparent; padding: 0.5rem 1rem; border-radius: 0.5rem; cursor: pointer; font-size: 0.875rem; transition: all 0.2s ease;"
      >
        <i v-if="checkingTargetCount" class="pi pi-spinner pi-spin mr-2"></i>
        <i v-else class="pi pi-users mr-2"></i>
        {{ checkingTargetCount ? 'Checking...' : 'Check audience size' }}
      </button>
    </div>
    
    <!-- Audience size result display -->
    <div v-if="audienceResult" class="audience-result mt-3">
      <div class="result-header">
        <i class="pi pi-check-circle mr-2 text-green-600"></i>
        <span class="font-semibold text-green-700">Audience Size Result</span>
      </div>
      <div class="result-content mt-2">
        <div class="stat-item">
          <span class="stat-label">Target Users: </span>
          <span class="stat-value">{{ audienceResult.totalTargetedUsers.toLocaleString() }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Age Range: </span>
          <span class="stat-value">{{ audienceResult.minAge }}-{{ audienceResult.maxAge }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">Target Type: </span>
          <span class="stat-value">{{ audienceResult.targetType }}</span>
        </div>
      </div>
    </div>
    
    <!-- Error display -->
    <div v-if="audienceError" class="audience-error mt-3">
      <div class="error-header">
        <i class="pi pi-exclamation-triangle mr-2 text-red-600"></i>
        <span class="font-semibold text-red-700">Error</span>
      </div>
      <p class="error-message mt-1">{{ audienceError }}</p>
    </div>
    
    <p v-if="!audienceResult && !audienceError" class="text-xs text-blue-600">
      Click the button above to see how many users your ad will target based on current settings.
    </p>
  </div>
</template>

<script>
import { ref } from "vue";
import { post as getTargetedUsersCount } from "@/api/mediaspace/getTargetedUsersCount";

export default {
  components: {},
  props: {
    context: {
      type: Object,
      required: true
    },
    field: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const checkingTargetCount = ref(false);
    const audienceResult = ref(null);
    const audienceError = ref(null);

    const checkTargetedUsersCount = async () => {
      try {
        checkingTargetCount.value = true;
        audienceResult.value = null;
        audienceError.value = null;
        
        const request = {
          minAge: props.context.targetMinAge || 0,
          maxAge: props.context.targetMaxAge || 100,
          targetType: props.context.targetType || "All"
        };

        const response = await getTargetedUsersCount(request);
        audienceResult.value = response;
      } catch (error) {
        console.error('Error getting targeted users count:', error);
        audienceError.value = "Failed to get targeted users count. Please try again.";
      } finally {
        checkingTargetCount.value = false;
      }
    };

    return {
      checkTargetedUsersCount,
      checkingTargetCount,
      audienceResult,
      audienceError,
    };
  },
};
</script>

<style scoped>
/* Audience preview section styling */
.audience-preview-section {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 0.75rem;
  padding: 1rem;
  margin: 1rem 0;
}

.audience-preview-section h4 {
  color: #0369a1;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.audience-preview-section p {
  color: #0c4a6e;
  font-size: 0.75rem;
  line-height: 1.4;
}

.custom-button:hover {
  background: #3b82f6 !important;
  color: white !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.custom-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.custom-button:disabled:hover {
  background: transparent !important;
  color: #3b82f6 !important;
  transform: none;
  box-shadow: none;
}

/* Audience result styling */
.audience-result {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 0.5rem;
  padding: 0.75rem;
}

.result-header {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
}

.result-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
}

.stat-label {
  color: #065f46;
  font-weight: 500;
}

.stat-value {
  color: #047857;
  font-weight: 600;
  background: rgba(34, 197, 94, 0.15);
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}

/* Error styling */
.audience-error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 0.5rem;
  padding: 0.75rem;
}

.error-header {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
}

.error-message {
  color: #991b1b;
  font-size: 0.75rem;
  margin: 0;
}
</style> 